import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import { Card, CardContent } from '../components/Card';
import { Calendar, Clock, User, Tag, ArrowRight, ArrowLeft, BookOpen } from 'lucide-react';
import Button from '../components/Button';
import { getPostBySlug, getRelatedPosts } from '../data/blogPosts';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  // If post not found, redirect to blog page
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = getRelatedPosts(post, 3);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('de-DE', options);
  };

  return (
    <MainLayout
      title={`${post.title} | SENKAY Blog`}
      description={post.metaDescription}
      keywords={post.keywords}
    >
      {/* Breadcrumb */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <nav className="flex items-center space-x-2 text-sm text-primary-text-muted">
          <Link to="/" className="hover:text-primary-blue-mid transition-colors">
            Home
          </Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-primary-blue-mid transition-colors">
            Blog
          </Link>
          <span>/</span>
          <span className="text-primary-text-dark">{post.category}</span>
        </nav>
      </section>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
        {/* Category Badge */}
        <div className="mb-4">
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary-blue-light text-primary-blue-mid text-xs font-semibold">
            <Tag className="w-3 h-3 mr-1.5" />
            {post.category}
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-bold text-primary-text-dark mb-4 leading-tight">
          {post.title}
        </h1>

        {/* Excerpt */}
        <p className="text-base text-primary-text-muted mb-6 leading-relaxed">
          {post.excerpt}
        </p>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-4 pb-8 mb-8 border-b border-primary-gray-border">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-blue-light to-primary-blue-mid flex items-center justify-center mr-2.5">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold text-primary-text-dark">{post.author}</div>
              <div className="text-xs text-primary-text-muted">{post.authorRole}</div>
            </div>
          </div>

          <div className="flex items-center text-sm text-primary-text-muted">
            <Calendar className="w-4 h-4 mr-1.5" />
            <span>{formatDate(post.date)}</span>
          </div>

          <div className="flex items-center text-sm text-primary-text-muted">
            <Clock className="w-4 h-4 mr-1.5" />
            <span>{post.readTime} Lesezeit</span>
          </div>
        </div>

        {/* Featured Image Placeholder */}
        <div className="w-full h-48 md:h-64 mb-12 rounded-xl bg-gradient-to-br from-primary-blue-light to-primary-blue-mid flex items-center justify-center shadow-soft">
          <BookOpen className="w-16 h-16 text-white opacity-20" />
        </div>

        {/* Article Content - Professional Reading Container */}
        <div className="bg-white rounded-xl shadow-soft border border-primary-gray-border p-6 md:p-8 mb-12">
          <div
            className="prose prose-base max-w-none
              prose-headings:text-primary-text-dark
              prose-headings:font-normal
              prose-headings:tracking-normal
              prose-h2:text-xs
              prose-h2:uppercase
              prose-h2:mt-12
              prose-h2:mb-4
              prose-h3:text-[0.7rem]
              prose-h3:font-medium
              prose-h3:mt-8
              prose-h3:mb-3
              prose-h4:text-xs
              prose-h4:font-normal
              prose-h4:mt-6
              prose-h4:mb-2
              prose-p:text-base
              prose-p:text-primary-text-dark
              prose-p:leading-relaxed
              prose-p:mb-6
              prose-ul:my-6
              prose-ul:space-y-3
              prose-ol:my-6
              prose-ol:space-y-3
              prose-li:text-base
              prose-li:text-primary-text-dark
              prose-li:leading-relaxed
              prose-strong:text-primary-text-dark
              prose-strong:font-semibold
              prose-a:text-primary-blue-mid
              prose-a:no-underline
              prose-a:transition-colors
              hover:prose-a:text-primary-blue-dark
              hover:prose-a:underline
              prose-blockquote:border-l-2
              prose-blockquote:border-primary-blue-mid
              prose-blockquote:pl-4
              prose-blockquote:py-2
              prose-blockquote:my-4
              prose-blockquote:text-primary-text-muted
              prose-blockquote:italic
              prose-code:bg-primary-skin
              prose-code:text-primary-text-dark
              prose-code:px-1.5
              prose-code:py-0.5
              prose-code:rounded
              prose-code:text-sm
              prose-code:before:content-['']
              prose-code:after:content-['']
              prose-hr:border-primary-gray-border
              prose-hr:my-6
              prose-img:rounded-lg
              prose-img:my-4"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          <div className="mt-8 pt-6 border-t border-primary-gray-border">
            <h3 className="text-xs font-semibold text-primary-text-muted uppercase tracking-wide mb-2">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full bg-primary-skin text-primary-text-dark text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Author Info Card */}
        <Card className="mt-12">
          <CardContent className="p-5">
            <div className="flex items-start space-x-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-blue-light to-primary-blue-mid flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-primary-text-dark mb-1">
                  {post.author}
                </h3>
                <p className="text-sm text-primary-blue-mid font-medium mb-2">
                  {post.authorRole}
                </p>
                <p className="text-sm text-primary-text-muted leading-relaxed">
                  Experte für {post.category} bei SENKAY Personal und Logistik.
                  Mehrjährige Erfahrung in der Branche und Leidenschaft für professionelle Lösungen.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mt-12">
          <Link to="/blog" className="flex-1">
            <Button variant="outline" icon={ArrowLeft} className="w-full">
              Zurück zur Übersicht
            </Button>
          </Link>
          <Link to="/kontakt" className="flex-1">
            <Button variant="primary" icon={ArrowRight} iconPosition="right" className="w-full">
              Jetzt Kontakt aufnehmen
            </Button>
          </Link>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-primary-skin/20">
          <div className="mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-primary-text-dark mb-2">
              Das könnte Sie auch interessieren
            </h2>
            <p className="text-sm text-primary-text-muted">
              Weitere Artikel aus dem SENKAY Blog
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedPosts.map((relatedPost) => (
              <Card key={relatedPost.id} hover={true}>
                <CardContent className="p-0">
                  {/* Image Placeholder */}
                  <div className="w-full h-40 bg-gradient-to-br from-primary-skin to-primary-blue-light flex items-center justify-center">
                    <BookOpen className="w-12 h-12 text-primary-blue-mid opacity-20" />
                  </div>

                  <div className="p-5">
                    {/* Category Badge */}
                    <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary-blue-light text-primary-blue-mid text-xs font-semibold mb-2">
                      <Tag className="w-3 h-3 mr-1" />
                      {relatedPost.category}
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-primary-text-dark mb-3 line-clamp-2">
                      {relatedPost.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-sm text-primary-text-muted mb-4 line-clamp-3">
                      {relatedPost.excerpt}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center justify-between text-xs text-primary-text-muted mb-4">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(relatedPost.date)}
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {relatedPost.readTime}
                      </div>
                    </div>

                    {/* Read More Link */}
                    <Link to={`/blog/${relatedPost.slug}`}>
                      <Button variant="outline" size="sm" icon={ArrowRight} iconPosition="right" className="w-full">
                        Weiterlesen
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

    </MainLayout>
  );
};

export default BlogPostPage;
